// @ts-nocheck

import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import { BackSvg } from '../../../assets/svg/numeric-keyboard';
import { getDecimalSeparator } from '../../../i18n/l10n';
import theme from '../../../styled-components/dark';

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const Cell = styled.div`
  flex: 1;
  margin: 0.03em;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 2.05em;
  font-size: 1.4em;

  font-family: 'BettingArchivoNarrow';
  font-weight: bold;
  transition: none 75ms ease-in-out;
  transition-property: background-color, color;

  cursor: pointer;

  ${({ $isSpecial, $disabled }) => {
    if ($disabled) {
      return css`
        color: ${theme.NumericKeyboard.disabled.color};
        background-color: ${theme.NumericKeyboard.disabled.backgroundColor};
        pointer-events: none;
      `;
    } else {
      const activeStyle = css`
        &:active {
          fill: white;
          color: ${theme.NumericKeyboard.active.color};
          background-color: ${theme.NumericKeyboard.active.backgroundColor};
        }
      `;
      if ($isSpecial) {
        return css`
          color: ${theme.NumericKeyboard.special.color};
          background-color: ${theme.NumericKeyboard.special.backgroundColor};
          ${activeStyle}
        `;
      }
      return css`
        color: ${theme.NumericKeyboard.default.color};
        background-color: ${theme.NumericKeyboard.default.backgroundColor};
        ${activeStyle}
      `;
    }
  }}
`;

const StyledBackSvg = styled(BackSvg)`
  width: 1.2em;
`;

export type NumericKeyboardProps = {
  className?: string;
  value: string;
  onInputChange: (value: string) => void;
};

const inputs = [
  [1, 2, 3],
  [4, 5, 6],
  [7, 8, 9],
];

const NumericKeyboard = forwardRef<NumericKeyboardProps, Grid>(function NumericKeyboard(
  { className, value = '', onInputChange },
  ref,
) {
  const decimalSeparator = useMemo(() => getDecimalSeparator(), []);
  const valueLength = value?.length ?? 0;
  const shouldDisableDecimalSeparatorCell = !!~value.indexOf('.');

  const [isFirstClick, setIsFirstClick] = useState(true);

  const onCellClick = useCallback(
    (inputDigit: number | string) => () => {
      if (!isFirstClick) {
        onInputChange(value + inputDigit);
      } else {
        setIsFirstClick(false);
        onInputChange(`${inputDigit}`);
      }
    },
    [value, onInputChange, isFirstClick],
  );

  const onDecimalSeparatorClick = useCallback(() => {
    if (isFirstClick) {
      setIsFirstClick(false);
    }

    onInputChange(value.concat(valueLength ? '.' : '0.'));
  }, [value, isFirstClick, onInputChange, valueLength]);

  const onBackspaceClick = useCallback(() => {
    onInputChange(value.slice(0, valueLength - 1));
  }, [onInputChange, value, valueLength]);

  const numericalKeys = useMemo(
    () =>
      inputs.map((row, i) => (
        <Row key={`grid-row-${i}`}>
          {row.map((input, j) => (
            <Cell key={`grid-row-${i}-cell-${j}`} onClick={onCellClick(input)}>
              {input}
            </Cell>
          ))}
        </Row>
      )),
    [onCellClick],
  );

  const bottomKeys = useMemo(
    () => (
      <Row>
        <Cell onClick={onDecimalSeparatorClick} $disabled={shouldDisableDecimalSeparatorCell}>
          {decimalSeparator}
        </Cell>
        <Cell onClick={onCellClick(0)}>0</Cell>
        <Cell onClick={onBackspaceClick} $isSpecial $disabled={!valueLength}>
          <StyledBackSvg />
        </Cell>
      </Row>
    ),
    [
      decimalSeparator,
      onBackspaceClick,
      onCellClick,
      onDecimalSeparatorClick,
      shouldDisableDecimalSeparatorCell,
      valueLength,
    ],
  );

  return (
    <Grid className={className} ref={ref}>
      {numericalKeys}
      {bottomKeys}
    </Grid>
  );
});

export default NumericKeyboard;
